import { KeyRound, MessageSquare, PlusCircle, ChevronRight } from 'lucide-react'
import type { ChatChoice, ChatChoiceSet } from '../../../netlify/functions/lib/project-chat-types'

// A set of clickable choices the agent (or a built-in flow, e.g. hitting a login wall) offers the
// user, rendered as full-width rows instead of asking them to type a reply — replacing the old "read
// a bulleted list and type your answer" interaction. Each row shows the option label and, when
// provided, a one-line description of what it does, mirroring how nut.new presents chat choices (and
// other onboarding choices). This component is purely presentational: it
// draws the prompt + rows and calls `onSelect` with the chosen option. The host decides what each
// does from `choice.action` — a `ui` action reveals inline UI (the add-logins form) with no model
// round-trip, a `message` action sends the agent a specific message.

function choiceIcon(choice: ChatChoice) {
  if (choice.action.kind === 'ui' && choice.action.ui === 'logins') return <KeyRound size={16} strokeWidth={2} aria-hidden />
  if (choice.id === 'create-account') return <PlusCircle size={16} strokeWidth={2} aria-hidden />
  return <MessageSquare size={16} strokeWidth={2} aria-hidden />
}

export default function ChatChoices({
  choiceSet,
  onSelect,
  disabled,
}: {
  choiceSet: ChatChoiceSet
  onSelect: (choice: ChatChoice) => void
  disabled?: boolean
}) {
  if (!choiceSet.choices.length) return null
  return (
    <div className="chat-choices" data-testid="chat-choices">
      {choiceSet.prompt && <p className="chat-choices__prompt">{choiceSet.prompt}</p>}
      <div className="chat-choices__list" role="group" aria-label="Choices">
        {choiceSet.choices.map((choice) => (
          <button
            key={choice.id}
            type="button"
            className="chat-choice"
            onClick={() => onSelect(choice)}
            disabled={disabled}
            data-testid={`chat-choice-${choice.id}`}
            data-analytics-event="chat-choice-select"
          >
            <span className="chat-choice__icon">{choiceIcon(choice)}</span>
            <span className="chat-choice__body">
              <span className="chat-choice__label">{choice.label}</span>
              {choice.description && <span className="chat-choice__desc">{choice.description}</span>}
            </span>
            <ChevronRight size={16} strokeWidth={2} className="chat-choice__chevron" aria-hidden />
          </button>
        ))}
      </div>
    </div>
  )
}
